import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, ColorPicker } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";

import {
  getCategoriesApi,
  createCategoryApi,
  updateCategoryApi,
  deleteCategoryApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";

const ACCENT = "#534ab7";

// Ionicons names rendered by the admin/mobile clients — kept in sync manually
// since the backend just stores the icon name as a plain string.
const ICON_OPTIONS = [
  "nutrition-outline", "water-outline", "shirt-outline", "game-controller-outline",
  "sparkles-outline", "heart-outline", "bed-outline", "pricetag-outline",
  "basket-outline", "gift-outline", "book-outline", "paw-outline",
  "moon-outline", "sunny-outline", "happy-outline", "grid-outline",
];

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [search,     setSearch]     = useState("");
  const [open,       setOpen]       = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [form] = Form.useForm();

  const fetchCategories = async () => {
    try {
      const res = await getCategoriesApi();
      setCategories((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load categories");
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const displayed = categories.filter(
    (c) => !search || c.name?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, icon: ICON_OPTIONS[0], color: ACCENT });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name:     record.name,
      icon:     record.icon || ICON_OPTIONS[0],
      color:    record.color || ACCENT,
      isActive: record.active,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        name:   values.name,
        icon:   values.icon,
        color:  typeof values.color === "string" ? values.color : values.color?.toHexString(),
        active: values.isActive,
      };
      if (editing) {
        await updateCategoryApi(editing.id, payload);
        message.success("Category updated");
      } else {
        await createCategoryApi(payload);
        message.success("Category added");
      }
      setOpen(false);
      fetchCategories();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save category");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateCategoryApi(record.id, {
        name:   record.name,
        icon:   record.icon,
        color:  record.color,
        active: !record.active,
      });
      message.success(record.active ? "Category deactivated" : "Category activated");
      fetchCategories();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this category?",
      content: "Products in this category will be left without a category.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteCategoryApi(id);
          message.success("Category deleted");
          fetchCategories();
        } catch {
          message.error("Failed to delete category");
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (v, r) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, color: "#111827" }}>
          <span
            style={{
              width: 22, height: 22, borderRadius: 6, flexShrink: 0,
              background: (r.color || ACCENT) + "20",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, color: r.color || ACCENT,
            }}
          >
            ●
          </span>
          {v}
        </span>
      ),
    },
    {
      title: "Icon",
      dataIndex: "icon",
      width: 160,
      render: (v) => v ? <Tag>{v}</Tag> : <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 100,
      align: "center",
      render: (v) => v ? <Tag color="green">Active</Tag> : <Tag color="default">Off</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Categories</div>
          <div className="page-sub">{categories.length} categories — used by Products, admin and mobile</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Category
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search categories…"
          allowClear
          style={{ width: 280, borderRadius: 8 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <AppTable columns={columns} data={displayed} />

      <Modal
        title={editing ? `Edit — ${editing.name}` : "Add Category"}
        open={open}
        okText={editing ? "Save Changes" : "Add Category"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput
            name="name"
            label="Category Name"
            rules={[{ required: true, message: "Name is required" }]}
            placeholder="e.g. Feeding, Toys, Clothing"
          />

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppSelect
                name="icon"
                label="Icon"
                rules={[{ required: true, message: "Pick an icon" }]}
                options={ICON_OPTIONS.map((i) => ({ value: i, label: i }))}
              />
            </div>
            <Form.Item name="color" label="Color" rules={[{ required: true, message: "Pick a color" }]}>
              <ColorPicker format="hex" />
            </Form.Item>
          </div>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
